import { renderAsync } from 'docx-preview';
import html2canvas from 'html2canvas';

/** A4 aspect ratio (210:297) - render at reasonable size for quality */
const RENDER_WIDTH = 400;
const RENDER_HEIGHT = 565;

/**
 * Render DOCX first page to canvas at given dimensions (fit within, preserve aspect ratio).
 * Uses docx-preview to render to HTML, then html2canvas to capture.
 */
export async function renderDocxFirstPageToCanvas(
    file: File,
    targetWidth: number,
    targetHeight: number
): Promise<string> {
    const pixelRatio = 2;

    const container = document.createElement('div');
    const containerId = `docx-container-${Date.now()}`;
    container.id = containerId;
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = `${RENDER_WIDTH}px`;
    container.style.height = `${RENDER_HEIGHT}px`;
    container.style.overflow = 'hidden';
    container.style.backgroundColor = '#ffffff';
    document.body.appendChild(container);

    try {
        await renderAsync(file, container, container, {
            breakPages: true,
            ignoreLastRenderedPageBreak: false,
            inWrapper: true,
        });

        const style = document.createElement('style');
        style.innerHTML = `
            #${containerId} .docx-wrapper {
                box-shadow: none;
                padding: 0;
                margin: 0;
                background: #ffffff !important;
                width: 100%;
            }
            #${containerId} .docx {
                padding: 16px;
                margin: 0 !important;
                width: 100% !important;
                min-height: auto !important;
                box-shadow: none !important;
                background: #ffffff !important;
            }
        `;
        container.appendChild(style);

        const canvas = await html2canvas(container, {
            scale: pixelRatio,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
        });

        const scale = Math.min(
            targetWidth / canvas.width,
            targetHeight / canvas.height
        );
        const scaledWidth = Math.round(canvas.width * scale);
        const scaledHeight = Math.round(canvas.height * scale);

        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = Math.floor(scaledWidth * pixelRatio);
        outputCanvas.height = Math.floor(scaledHeight * pixelRatio);
        const ctx = outputCanvas.getContext('2d');

        if (!ctx) {
            throw new Error('Failed to get canvas context');
        }

        // Keep logical output size while using a denser backing store.
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, scaledWidth, scaledHeight);
        ctx.drawImage(canvas, 0, 0, scaledWidth, scaledHeight);

        return outputCanvas.toDataURL('image/png');
    } finally {
        document.body.removeChild(container);
    }
}
